/**
 * Ticket Chat Screen — conversation view for a single ticket.
 * Shows chat messages only (no calls/SMS). Refetches the timeline when a
 * `ticket:message` WS event arrives for this ticket.
 * Closed/resolved tickets show a banner and disable input.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    Keyboard,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Snackbar, Text } from "react-native-paper";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";

import { TicketChatBubble, type ChatBubbleMessage } from "@/components/tickets/TicketChatBubble";
import { TicketChatInput } from "@/components/tickets/TicketChatInput";
import { TicketVisitBanner } from "@/components/tickets/TicketVisitBanner";
import { fetchVisitPreview, type VisitPreview } from "@/lib/api";
import { colors, fonts, spacing } from "@/lib/theme";
import {
    fetchTicketTimeline,
    markTicketRead,
    sendTicketMessage,
    type Attachment,
    type TicketChatMessage,
    type TicketHeader
} from "@/lib/ticket-api";
import { useWebSocketEvent, useWebSocketReconnect } from "@/lib/websocket";

// ─── Helpers ─────────────────────────────────────────

function chatMessagesToBubbles(items: Array<{ type: string; data: unknown }>): ChatBubbleMessage[] {
    return items
        .filter((item) => item.type === "chat_message")
        .map((item) => {
            const msg = item.data as TicketChatMessage;
            return {
                id: String(msg.id),
                content: msg.content ?? "",
                senderType: msg.senderType,
                senderName: msg.senderName,
                createdAt: msg.createdAt,
                deliveryStatus: "delivered" as const,
                attachments: msg.attachments ?? undefined
            };
        });
}

const ANDROID_IME_ACCESSORY_OFFSET = 56;

// ─── Screen ──────────────────────────────────────────

export default function TicketChatScreen() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();
    const ticketId = Number(id);
    const flatListRef = useRef<FlatList>(null);
    const insets = useSafeAreaInsets();
    const safeAreaEdges = Platform.OS === "ios" ? (["top", "bottom"] as const) : (["top"] as const);

    const [ticket, setTicket] = useState<TicketHeader | null>(null);
    const [messages, setMessages] = useState<ChatBubbleMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [androidKeyboardInset, setAndroidKeyboardInset] = useState(0);
    const [visitPreview, setVisitPreview] = useState<VisitPreview | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    const loadMessages = useCallback(async () => {
        try {
            const result = await fetchTicketTimeline(ticketId);
            setTicket(result.data.ticket);
            const chatMessages = chatMessagesToBubbles(result.data.items);

            setMessages((prev) => {
                // Server is authoritative for confirmed ids. Append any
                // optimistic local-* messages the server hasn't echoed yet.
                const confirmedIds = new Set(chatMessages.map((m) => m.id));
                const pendingLocal = prev.filter(
                    (m) => m.id.startsWith("local-") && !confirmedIds.has(m.id)
                );
                return [...chatMessages, ...pendingLocal];
            });
        } catch {
            // Silently handle refetch errors
        } finally {
            setLoading(false);
        }
    }, [ticketId]);

    // Initial load + mark as read
    useEffect(() => {
        loadMessages();
        markTicketRead(ticketId).catch(() => {});
    }, [loadMessages, ticketId]);

    // Load the linked-visit preview for the banner. Errors are swallowed —
    // the banner just doesn't render if the visit is missing or unreachable.
    useEffect(() => {
        const visitId = ticket?.visitId;
        if (!visitId) {
            setVisitPreview(null);
            return;
        }
        let cancelled = false;
        fetchVisitPreview(visitId)
            .then((p) => {
                if (!cancelled) {
                    setVisitPreview(p);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setVisitPreview(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [ticket?.visitId]);

    const handleVisitBannerPress = () => {
        if (!visitPreview) {
            return;
        }
        if (visitPreview.viewable) {
            router.push(`/visit/${visitPreview.id}`);
        } else {
            setSnackbarMessage("This visit is not available anymore.");
        }
    };

    // Refetch when a new message lands on this ticket (real-time via WebSocket)
    useWebSocketEvent("ticket:message", (data) => {
        if (data.ticketId === ticketId) {
            loadMessages();
        }
    });
    // Refetch on non-message updates (status, handler, escalation) so the
    // closed banner / handler state stays in sync without a manual reload.
    useWebSocketEvent("ticket:updated", (data) => {
        if (data.ticketId === ticketId) {
            loadMessages();
        }
    });
    // Catch up after the socket reconnects (app foregrounded, network drop, or a
    // dead socket the heartbeat just rescued). Messages sent while the socket was
    // down never arrived as live `ticket:message` events — without this the user
    // sees the push notification but a stale thread until they remount the screen.
    useWebSocketReconnect(loadMessages);

    // Auto-scroll on new messages
    useEffect(() => {
        if (messages.length > 0) {
            setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
        }
    }, [messages.length]);

    useEffect(() => {
        if (Platform.OS !== "android") {
            return;
        }

        const showSub = Keyboard.addListener("keyboardDidShow", (event) => {
            setAndroidKeyboardInset(Math.max(0, event.endCoordinates.height));
            setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 50);
        });
        const hideSub = Keyboard.addListener("keyboardDidHide", () => {
            setAndroidKeyboardInset(0);
        });

        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, []);

    const handleSend = async (text: string, attachments: Attachment[] = []) => {
        const localId = `local-${Date.now()}`;
        const optimistic: ChatBubbleMessage = {
            id: localId,
            content: text,
            senderType: "rn",
            senderName: null,
            createdAt: new Date().toISOString(),
            deliveryStatus: "sending",
            // The staged files are already in S3 but have no presigned viewUrl
            // yet, so the optimistic bubble shows file chips; the refetch after
            // the send swaps in real thumbnails.
            attachments: attachments.length > 0 ? attachments : undefined
        };

        setMessages((prev) => [...prev, optimistic]);

        try {
            const result = await sendTicketMessage(ticketId, text, attachments);
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === localId
                        ? {
                              ...m,
                              id: String(result.data.id),
                              createdAt: result.data.createdAt,
                              deliveryStatus: "delivered" as const
                          }
                        : m
                )
            );
            // The optimistic bubble holds local file references with no presigned
            // viewUrl, so an attachment send needs one refetch to render real
            // thumbnails. Text-only sends already look final — don't refetch.
            if (attachments.length > 0) {
                void loadMessages();
            }
        } catch {
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === localId ? { ...m, deliveryStatus: "failed" as const } : m
                )
            );
        }
    };

    const isClosed = ticket?.status === "closed" || ticket?.status === "resolved";
    const androidComposerLift =
        Platform.OS === "android"
            ? androidKeyboardInset > 0
                ? androidKeyboardInset + ANDROID_IME_ACCESSORY_OFFSET
                : 0
            : 0;

    return (
        <>
            <Stack.Screen
                options={{
                    headerShown: false
                }}
            />

            <SafeAreaView style={styles.container} edges={safeAreaEdges}>
                <KeyboardAvoidingView
                    style={styles.container}
                    behavior={Platform.OS === "ios" ? "padding" : undefined}
                    keyboardVerticalOffset={Platform.OS === "ios" ? 12 : 0}
                >
                    <View style={styles.header}>
                        <TouchableOpacity
                            onPress={() => router.back()}
                            activeOpacity={0.7}
                            style={styles.backButton}
                        >
                            <MaterialCommunityIcons
                                name="arrow-left"
                                size={24}
                                color={colors.textPrimary}
                            />
                        </TouchableOpacity>
                    </View>

                    {loading ? (
                        <View style={styles.center}>
                            <ActivityIndicator size="large" color={colors.teal500} />
                        </View>
                    ) : (
                        <>
                            {visitPreview && (
                                <TicketVisitBanner
                                    preview={visitPreview}
                                    onPress={handleVisitBannerPress}
                                />
                            )}
                            <FlatList
                                ref={flatListRef}
                                data={messages}
                                style={styles.list}
                                renderItem={({ item }) => <TicketChatBubble message={item} />}
                                keyExtractor={(item) => item.id}
                                contentContainerStyle={[
                                    styles.messageList,
                                    {
                                        paddingBottom:
                                            spacing["2xl"] +
                                            (Platform.OS === "ios"
                                                ? Math.max(insets.bottom, spacing.sm)
                                                : androidComposerLift + spacing["3xl"])
                                    }
                                ]}
                                onContentSizeChange={() =>
                                    flatListRef.current?.scrollToEnd({ animated: false })
                                }
                                keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "none"}
                                keyboardShouldPersistTaps="handled"
                                automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                                contentInsetAdjustmentBehavior="never"
                            />

                            {isClosed ? (
                                <View style={styles.closedBanner}>
                                    <MaterialCommunityIcons
                                        name="check-circle"
                                        size={16}
                                        color={colors.success}
                                    />
                                    <Text style={styles.closedText}>
                                        This conversation is {ticket?.status}
                                    </Text>
                                </View>
                            ) : (
                                <View
                                    style={[
                                        styles.inputDock,
                                        Platform.OS === "android" && {
                                            marginBottom: androidComposerLift
                                        }
                                    ]}
                                >
                                    <TicketChatInput ticketId={ticketId} onSend={handleSend} />
                                </View>
                            )}
                        </>
                    )}
                </KeyboardAvoidingView>
                <Snackbar
                    visible={!!snackbarMessage}
                    onDismiss={() => setSnackbarMessage(null)}
                    duration={3000}
                >
                    {snackbarMessage}
                </Snackbar>
            </SafeAreaView>
        </>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    center: { flex: 1, alignItems: "center", justifyContent: "center" },
    header: {
        paddingHorizontal: spacing.base,
        paddingTop: spacing.sm,
        paddingBottom: spacing.xs,
        backgroundColor: colors.background
    },
    backButton: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.surface
    },
    list: {
        flex: 1
    },
    messageList: { padding: spacing.base, paddingBottom: spacing["2xl"] },
    inputDock: {
        backgroundColor: colors.surface
    },
    closedBanner: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs,
        paddingVertical: spacing.md,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    closedText: { fontFamily: fonts.medium, fontSize: 14, color: colors.textSecondary }
});
